/**
 * SIM list toolbar with search, status filter, and bulk-delete controls.
 *
 * @module components/sim/EnhancedTableToolbar
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useState} from "react";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import InputBase from "@material-ui/core/InputBase";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import Tooltip from "@material-ui/core/Tooltip";
import SimsPermissions from "../../permissions/sims";
import StyledLink from "../common/StyledLink";
import AddIcon from "@material-ui/icons/Add";
import PropTypes from "prop-types";
import {permissionCheck} from "../../utils/permissionCheck";
import {useToolbarStyles} from "./EnhancedTableToolbar.js";

/**
 * Represents an enhanced toolbar component for managing and interacting with a table of simulators.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {Function} props.initializeNewSim - Function to initialize the creation of a new simulator.
 * @param {string} props.filterType - The current filter type applied to the simulator list.
 * @param {Function} props.setFilterType - Function to update the filter type.
 * @param {Function} props.setTextSearch - Function to set the text value for search operations.
 * @param {Array<Object>} props.products - Array of product objects to populate filter options.
 * @param {Function} props.setTrash - Function to toggle whether the trash view is active.
 * @param {boolean} props.trash - Indicates whether the component is in the trash view mode.
 * @param {Function} props.setEmptyTrash - Function to empty the trash.
 * @param {Array<Object>} props.simsList - List of simulator objects relevant to the current view.
 *
 * @returns {JSX.Element} A toolbar element with functionalities such as search, filtering, trash management,
 *                        and simulator creation.
 */
const EnhancedTableToolbar = ({
    initializeNewSim,
    filterType,
    setFilterType,
    setTextSearch,
    products,
    setTrash,
    trash,
    setEmptyTrash,
    simsList,
}) => {
    const classes = useToolbarStyles();
    const [text, setText] = useState("");

    /**
     * Handles search functionality triggered by user input events.
     *
     * The function listens for specific user actions, such as pressing the "Enter" key
     * or a mouse click, to initiate a search operation. Upon detection of these events,
     * it updates the search text using the `setTextSearch` function.
     *
     * @param {Object} event - The event object representing user interaction.
     * @param {string} event.key - The key pressed during the keydown event.
     * @param {string} event.type - The type of the event (e.g., "mousedown").
     */
    const search = (event) => {
        if (event.key === "Enter" || (event.type === "mousedown")) {
            setTextSearch(text);
        }
    };

    /**
     * Handles the change event for an input element and updates the filter type value.
     *
     * @param {Object} event - The event object triggered by the change event.
     * @param {Object} event.target - The target element that triggered the event.
     * @param {string} event.target.value - The current value of the target element.
     */
    const handleChange = (event) => {
        setFilterType(event.target.value);
    };

    /**
     * Toggles the state of the `trash` variable.
     *
     * The function inverses the current boolean state of the `trash` variable
     * by calling the `setTrash` function with the negated value of `trash`.
     */
    const trashSims = () => {
        setTrash(!trash);
    };

    /**
     * A function that triggers the action to mark the trash as empty.
     * It sets the state indicating that the trash has been emptied.
     */
    const emptyTrash = () => {
        setEmptyTrash(true);
    };

    return (
        <Toolbar className={classes.root}>
            <Typography
                className={classes.title}
                variant="h6"
                id="tableTitle"
                component="div"
            >
                {trash ? 'Deleted Sims and Create Simulators' : 'Manage and Create Simulators'}
            </Typography>
            {trash ? '' : <div className={classes.search}>
                <InputBase
                    placeholder="Search Simulators"
                    classes={{
                        root: classes.inputRoot, input: classes.inputInput,
                    }}
                    inputprops={{"aria-label": "search"}}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => search(e)}
                />
                <div>
                    <Button onMouseDown={(e) => search(e)}><SearchIcon/></Button>
                </div>
            </div>}
            {trash ? '' : <FormControl variant="outlined" size="small">
                <Select
                    labelId="select-filter-type"
                    id="simple-select-filter"
                    value={filterType}
                    onChange={handleChange}
                    className={classes.select}
                >
                    <MenuItem value="All" selected>
                        All
                    </MenuItem>
                    {products.length > 0 && products.map((product) => (
                        <MenuItem key={product._id} value={product._id}>
                            {product.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>}
            {trash && simsList && simsList.length ? <Button
                className={classes.addNewButton}
                variant="contained"
                onClick={() => {
                    emptyTrash();
                }}
            >
                <DeleteForeverIcon className={classes.plusIcon}/>
                Empty Trash
            </Button> : ''}
            <Button
                variant="contained"
                className={classes.addNewButton}
                onClick={() => {
                    trashSims();
                }}
            >
                {trash ? 'SIMS' : 'TRASH'}
            </Button>
            <Tooltip
                title={permissionCheck(SimsPermissions.sim_list.create.task, SimsPermissions.sim_list.create.type) ? "" : "You don't have permission to create"}
                placement="bottom"
            >
                <span>
                    <StyledLink to="/sims/createsim">
                        <Button
                            className={classes.addNewButton}
                            variant="contained"
                            disabled={!permissionCheck(SimsPermissions.sim_list.create.task, SimsPermissions.sim_list.create.type)}
                            disableElevation
                            onClick={() => {
                                initializeNewSim();
                            }}
                        >
                            <AddIcon className={classes.plusIcon}/>
                            ADD NEW SIM
                        </Button>
                    </StyledLink>
                </span>
            </Tooltip>
        </Toolbar>
    );
};

EnhancedTableToolbar.propTypes = {
    initializeNewSim: PropTypes.func,
    filterType: PropTypes.string,
    setFilterType: PropTypes.func,
    setTextSearch: PropTypes.func,
    products: PropTypes.array,
    setTrash: PropTypes.func,
    trash: PropTypes.bool,
    setEmptyTrash: PropTypes.func,
    simsList: PropTypes.array,
};

export default EnhancedTableToolbar;
